<template>
  <section class="level-reader" :style="stageStyle">
    <div class="reader-shade"></div>
    <div class="reader-layout" :class="{ 'readonly-layout': activeSource !== 'random' }">
      <aside v-if="activeSource !== 'random'" class="readonly-banner">
        <div class="section-title">只读档案</div>
        <p>你是从档案入口进入该层级。此页面仅用于查看层级资料，不开放随机探索、占领、留言和聊天室。</p>
      </aside>

      <article class="level-brief">
        <button class="reader-back" @click="$emit('leave')">返回档案</button>
        <div class="brief-heading">
          <span>{{ sourceText }}</span>
          <small>Level {{ activeLevel.levelNum || activeLevel.id }}</small>
        </div>
        <h2>{{ activeLevel.title || activeLevel.name || '未命名层级' }}</h2>
        <p>{{ activeLevel.description || '暂无简介，谨慎进入。' }}</p>

        <div v-if="activeLevel.img" class="reader-cover">
          <div class="reader-cover-media">
            <img :src="activeLevel.img" :alt="activeLevel.name || activeLevel.title || '层级封面图'" />
          </div>
          <div>
            <span class="eyebrow">Cover Image</span>
            <strong>{{ activeLevel.title || activeLevel.name || `Level ${activeLevel.levelNum || activeLevel.id}` }}</strong>
          </div>
        </div>

        <div class="metrics">
          <div>
            <span>状态</span>
            <strong>{{ statusText(activeLevel.status) }}</strong>
          </div>
          <div>
            <span>访问</span>
            <strong>{{ activeLevel.enterNum || 0 }}</strong>
          </div>
          <div>
            <span>留言</span>
            <strong>{{ activeLevel.messageNum || 0 }}</strong>
          </div>
          <div>
            <span>收藏</span>
            <strong>{{ activeLevel.favoriteNum || 0 }}</strong>
          </div>
          <div>
            <span>上次巡逻</span>
            <strong>{{ formatDate(activeLevel.guardedAt) }}</strong>
          </div>
        </div>

        <div v-if="isOwner" class="patrol-strip">
          <span>巡逻规则：连续 {{ patrolExpireDays }} 天未巡逻后，该层级将自动变为无主层级。</span>
          <el-button type="primary" @click="$emit('patrol')">巡逻层级</el-button>
        </div>

        <div v-if="activeLevel.auditStatus === 1" class="audit-tip">层级资料正在等待管理员审核，通过后其他用户才可查看。</div>
        <div v-if="activeLevel.auditStatus === 3" class="audit-tip reject">层级资料已被驳回：{{ activeLevel.rejectReason || '请修改后重新提交审核。' }}</div>

        <div v-if="activeLevel.notice" class="notice">{{ activeLevel.notice }}</div>
        <section v-if="activeLevel.html" class="level-html" v-html="activeLevel.html"></section>
      </article>

      <aside v-if="activeSource === 'random'" class="action-panel">
        <div class="panel-actions">
          <el-button v-if="activeLevel.status === 0" class="panel-action-btn occupy-action" type="primary" :icon="Flag" @click="$emit('occupy')">占领层级</el-button>
          <el-button class="panel-action-btn favorite-action" :icon="Star" @click="$emit('favorite')">收藏</el-button>
          <el-button v-if="isOwner" class="panel-action-btn guard-action" :icon="Lock" @click="$emit('guard')">守护</el-button>
        </div>

        <div class="messages">
          <div class="section-title">留言记录</div>
          <div class="message-list">
            <div v-for="item in messages" :key="item.id" class="message-item">
              <strong>{{ item.username || '探索者' }}</strong>
              <p>{{ item.content }}</p>
            </div>
            <el-empty v-if="messages.length === 0" description="暂无留言" :image-size="64" />
          </div>
          <div class="message-input">
            <el-input :model-value="messageContent" placeholder="记录异常现象" maxlength="200" @update:model-value="$emit('update:messageContent', String($event))" />
            <el-button type="primary" @click="$emit('send')">发送</el-button>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Flag, Lock, Star } from '@element-plus/icons-vue';
import type { LevelItem, LevelSource } from './types';

const props = defineProps<{
  activeLevel: LevelItem;
  activeSource: LevelSource;
  sourceText: string;
  messages: any[];
  messageContent: string;
  isOwner: boolean;
  patrolExpireDays: number;
}>();

defineEmits<{
  (e: 'leave'): void;
  (e: 'occupy'): void;
  (e: 'favorite'): void;
  (e: 'guard'): void;
  (e: 'patrol'): void;
  (e: 'send'): void;
  (e: 'update:messageContent', value: string): void;
}>();

const stageStyle = computed(() => {
  const url = props.activeLevel.backgroundImg || props.activeLevel.img;
  return url ? { backgroundImage: `url(${url})` } : {};
});

const statusText = (status: number) => {
  if (status === 1) return '已占领';
  if (status === 2) return '待上线';
  if (status === -1) return '已删除';
  return '未占领';
};

const formatDate = (value?: string) => {
  if (!value) return '暂无';
  return String(value).replace('T', ' ').slice(0, 19);
};
</script>
